/**
 * Centralized URL / deep-link parsing module.
 *
 * Consolidates all query-param and hash parsing for auth callbacks and
 * OAuth redirects. Works with both web URLs (window.location) and native
 * deep-link URLs (Capacitor App plugin appUrlOpen events).
 *
 * Usage:
 *   // Web: pass window.location.search and window.location.hash
 *   const auth = parseRedirectAuthState(window.location.search, window.location.hash);
 *
 *   // Native deep link: parse from the incoming URL string
 *   App.addListener('appUrlOpen', (event) => {
 *     const u = new URL(event.url);
 *     const auth = parseRedirectAuthState(u.search, u.hash);
 *   });
 */

export type RedirectAuthState = {
  verified: boolean;
  recovery: boolean;
  email: string | null;
  /** Supabase error code returned on failed auth callback (e.g. "otp_expired") */
  authErrorCode: string | null;
  /** Supabase error type returned on failed auth callback (e.g. "access_denied") */
  authError: string | null;
};

export type OuraRedirectState = {
  connected: boolean;
  error: string | null;
};

/**
 * Parses email verification / auth success state from a URL.
 *
 * Supports:
 *   ?auth=verified          — custom flag from Supabase email template
 *   ?auth=recovery          — custom flag for password recovery
 *   ?verified=1             — alternate flag
 *   #type=signup            — Supabase PKCE implicit hash
 *   #type=recovery          — Supabase password recovery hash
 *
 * Also detects Supabase error callbacks, e.g.:
 *   ?error=access_denied&error_code=otp_expired&...
 *   #error=access_denied&error_code=otp_expired&...
 * These are returned by Supabase when an email link is expired or already used.
 */
export function parseRedirectAuthState(search: string, hash: string): RedirectAuthState {
  const params = new URLSearchParams(search);
  const hashRaw = hash.startsWith("#") ? hash.slice(1) : hash;
  const hashParams = new URLSearchParams(hashRaw);

  const verified =
    params.get("auth") === "verified" ||
    params.get("verified") === "1" ||
    hashParams.get("type") === "signup";

  const recovery =
    params.get("auth") === "recovery" ||
    hashParams.get("type") === "recovery";

  const email = params.get("email") ?? hashParams.get("email") ?? null;

  // Supabase puts errors in both query params and the hash fragment
  const authError = params.get("error") ?? hashParams.get("error") ?? null;
  const authErrorCode = params.get("error_code") ?? hashParams.get("error_code") ?? null;

  return { verified, recovery, email, authError, authErrorCode };
}

/**
 * Parses Oura OAuth result state from a URL.
 *
 * Supports:
 *   ?oura=connected         — success
 *   ?oura_error=<msg>       — error (URL-encoded)
 */
export function parseOuraRedirectState(search: string): OuraRedirectState {
  const params = new URLSearchParams(search);
  return {
    connected: params.get("oura") === "connected",
    error: params.get("oura_error") ?? null,
  };
}

/**
 * Clears all redirect flags from the browser URL bar (web only).
 * Call this after you've consumed the state to avoid re-triggering on refresh.
 *
 * Also clears Supabase error params (error, error_code, error_description) and
 * any auth-related hash fragment so the Supabase SDK doesn't re-process the
 * URL on subsequent page loads or refreshes.
 */
export function clearRedirectFlags(): void {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  let changed = false;

  // "code" is the PKCE auth code — clear it so stale/expired codes don't
  // cause repeated failed exchanges on every subsequent page load.
  for (const key of ["auth", "verified", "oura", "oura_error", "tab", "error", "error_code", "error_description", "code"]) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  // Clear the hash only when it contains error data. Do NOT clear the hash
  // when it contains session tokens (access_token, type=signup, etc.) —
  // the Supabase SDK needs to read those to establish the session (implicit
  // flow) and will clean the hash itself via replaceState afterward.
  if (url.hash) {
    const hashRaw = url.hash.startsWith("#") ? url.hash.slice(1) : url.hash;
    const hashParams = new URLSearchParams(hashRaw);
    if (hashParams.has("error") || hashParams.has("error_code")) {
      url.hash = "";
      changed = true;
    }
  }

  if (changed) {
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }
}
